import { useState, useEffect, createContext, useContext, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { ShoppingCart, Plus, Minus, Trash2, Clock, MapPin, Phone, ChevronLeft, Menu as MenuIcon, X, User, LogOut, Check, Loader2, Bell, Volume2, Sparkles } from "lucide-react";
import confetti from "canvas-confetti";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== PROMO MODAL BOULETTES ====================

const IMG_UNE_BOULETTE = "https://static.prod-images.emergentagent.com/jobs/d1916577-2152-462f-ae9a-eb99c2256591/images/9e4c86d6e92b97c6e4174364fa696590328e867eaaed132fa24765d638be5b2a.jpeg";
const IMG_DEUX_BOULETTES = "https://static.prod-images.emergentagent.com/jobs/d1916577-2152-462f-ae9a-eb99c2256591/images/88430ada0da16f5459c5d0bc73ac2abd9f5e296035be3b1f593c593aee659c20.jpeg";

const PromoModal = ({ onClose }) => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Fire confetti on modal open - multiple bursts for dramatic effect
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const colors = ['#FEA600', '#DC2626', '#171537', '#F8F4ED'];
    
    const randomInRange = (min, max) => Math.random() * (max - min) + min;
    
    // Initial big burst
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: colors
    });
    
    // Continuous confetti
    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      
      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }
      
      const particleCount = 50 * (timeLeft / duration);
      
      // Confetti from both sides
      confetti({
        particleCount: Math.floor(particleCount / 2),
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.7 },
        colors: colors
      });
      confetti({
        particleCount: Math.floor(particleCount / 2),
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.7 },
        colors: colors
      });
    }, 250);
    
    return () => clearInterval(interval);
  }, []);
  
  const handleDiscover = () => {
    onClose();
    navigate('/menu/snack');
  };
  
  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-fadeIn"
      style={{ backgroundColor: 'rgba(23, 21, 55, 0.95)' }}
      data-testid="promo-modal"
    >
      <div 
        className="relative bg-brand-cream border-4 border-brand-yellow rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl animate-scaleIn"
        style={{ animation: 'scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)' }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-2 bg-brand-navy/10 hover:bg-brand-navy/20 rounded-full transition-colors"
          data-testid="promo-close"
        >
          <X className="w-5 h-5 text-brand-navy" />
        </button>
        
        {/* Sparkle badge */}
        <div className="absolute top-4 left-4 z-10 bg-brand-red text-white px-3 py-1 rounded-full flex items-center gap-1 font-bold text-sm animate-pulse">
          <Sparkles className="w-4 h-4" />
          NOUVEAU
        </div>
        
        {/* Two images side by side */}
        <div className="grid grid-cols-2 gap-1 p-2 pt-12 bg-brand-navy">
          <div className="aspect-square overflow-hidden rounded-lg">
            <img
              src={IMG_UNE_BOULETTE}
              alt="Une boulette sauce lapin"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="aspect-square overflow-hidden rounded-lg">
            <img
              src={IMG_DEUX_BOULETTES}
              alt="Deux boulettes sauce lapin"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-brand-navy uppercase mb-3">
            C'est enfin là !
          </h2>
          <p className="text-brand-navy text-lg mb-2">
            <span className="font-bold text-brand-red">Boulettes sauce lapin</span>
          </p>
          <p className="text-brand-navy/70 text-sm mb-6">
            Notre spécialité tant attendue est disponible ! Découvrez nos boulettes accompagnées de notre sauce lapin unique.
          </p>
          
          {/* Price tags */}
          <div className="flex justify-center gap-4 mb-6">
            <div className="bg-brand-yellow/20 border border-brand-yellow rounded-lg px-4 py-2">
              <p className="text-xs text-brand-navy/60">1 boulette</p>
              <p className="font-mono font-bold text-brand-navy">4.50€</p>
            </div>
            <div className="bg-brand-yellow/20 border border-brand-yellow rounded-lg px-4 py-2">
              <p className="text-xs text-brand-navy/60">2 boulettes</p>
              <p className="font-mono font-bold text-brand-navy">8.00€</p>
            </div>
          </div>
          
          {/* CTA Button */}
          <button
            onClick={handleDiscover}
            className="w-full btn-primary py-4 text-lg font-heading uppercase tracking-wide flex items-center justify-center gap-2 hover:scale-105 transition-transform"
            data-testid="promo-discover-btn"
          >
            <Sparkles className="w-5 h-5" />
            Découvrir
          </button>
          
          <p className="text-xs text-brand-navy/40 mt-4">
            Aussi disponible : Boulette nature à 3.00€
          </p>
        </div>
      </div>
    </div>
  );
};

// ==================== SOUND ALERT ====================

const playOrderSound = () => {
  // Créer un son de bip caisse avec Web Audio API
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  
  const playBeep = (frequency, startTime, duration) => {
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = frequency;
    oscillator.type = 'square';
    
    gainNode.gain.setValueAtTime(0.3, startTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
    
    oscillator.start(startTime);
    oscillator.stop(startTime + duration);
  };
  
  // Séquence de bips style caisse enregistreuse
  const now = audioContext.currentTime;
  playBeep(1800, now, 0.1);
  playBeep(1800, now + 0.15, 0.1);
  playBeep(2200, now + 0.3, 0.2);
};

// ==================== CONTEXT ====================

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem("friteur_cart");
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem("friteur_cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(i => 
        i.product_id === item.product_id && 
        JSON.stringify(i.options) === JSON.stringify(item.options)
      );
      if (existing) {
        return prev.map(i => 
          i === existing 
            ? { ...i, quantity: i.quantity + item.quantity, total_price: (i.quantity + item.quantity) * i.unit_price }
            : i
        );
      }
      return [...prev, item];
    });
    toast.success("Ajouté au panier");
  };

  const updateQuantity = (index, quantity) => {
    if (quantity <= 0) {
      removeFromCart(index);
      return;
    }
    setCart(prev => prev.map((item, i) => 
      i === index 
        ? { ...item, quantity, total_price: quantity * item.unit_price }
        : item
    ));
  };

  const removeFromCart = (index) => {
    setCart(prev => prev.filter((_, i) => i !== index));
    toast.info("Retiré du panier");
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem("friteur_cart");
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.total_price, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, addToCart, updateQuantity, removeFromCart, clearCart, cartTotal, cartCount }}>
      {children}
    </CartContext.Provider>
  );
};

// ==================== COMPONENTS ====================

const PromoBanner = () => {
  const navigate = useNavigate();
  
  return (
    <div 
      className="bg-brand-red text-white py-2 px-4 cursor-pointer hover:bg-red-700 transition-colors"
      onClick={() => navigate('/menu/snack')}
      data-testid="promo-banner"
    >
      <div className="max-w-6xl mx-auto flex items-center justify-center gap-2 text-sm">
        <Sparkles className="w-4 h-4 text-brand-yellow flex-shrink-0" />
        <span className="text-center">
          <span className="font-bold">NOUVEAU !</span> Retrouvez nos délicieuses boulettes sauce lapin dans la catégorie <span className="font-bold underline">"Snack"</span>
        </span>
        <Sparkles className="w-4 h-4 text-brand-yellow flex-shrink-0" />
      </div>
    </div>
  );
};

const Header = ({ showBack = false, title = null }) => {
  const navigate = useNavigate();
  const { cartCount } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      {/* Promo Banner - Always visible */}
      <PromoBanner />
      
      <header className="sticky top-0 z-50 bg-brand-cream border-b-2 border-brand-navy">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          {showBack ? (
            <button onClick={() => navigate(-1)} className="p-2 -ml-2" data-testid="back-button">
              <ChevronLeft className="w-6 h-6 text-brand-navy" />
            </button>
          ) : (
            <button onClick={() => setMenuOpen(!menuOpen)} className="p-2 -ml-2 md:hidden" data-testid="menu-toggle">
              {menuOpen ? <X className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
            </button>
          )}
          
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_d1916577-2152-462f-ae9a-eb99c2256591/artifacts/9y6urvi0_LOGO_PRINCIPAL_FIRESAUCE%403x.png" 
              alt="FRITEUR" 
              className="h-10"
            />
          </Link>

          <div className="flex items-center gap-4">
            <Link 
              to="/cart" 
              className="relative p-2"
              data-testid="cart-button"
            >
              <ShoppingCart className="w-6 h-6 text-brand-navy" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-brand-red text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <nav className="md:hidden bg-brand-navy text-white p-4 border-t-2 border-brand-yellow">
            <Link to="/" className="block py-2 font-heading text-xl" onClick={() => setMenuOpen(false)}>ACCUEIL</Link>
            <Link to="/admin" className="block py-2 font-heading text-xl" onClick={() => setMenuOpen(false)}>ADMIN</Link>
          </nav>
        )}
      </header>
    </>
  );
};

const CategoryCard = ({ category, onClick }) => (
  <button
    onClick={onClick}
    className="relative aspect-square overflow-hidden border-2 border-brand-navy shadow-hard hover:shadow-hard-hover transition-all rounded-lg group"
    data-testid={`category-${category.slug}`}
  >
    <img 
      src={category.image} 
      alt={category.name}
      className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/90 via-brand-navy/30 to-transparent" />
    <div className="absolute bottom-0 left-0 right-0 p-4">
      <h3 className="font-heading text-2xl md:text-3xl text-white uppercase tracking-wide">
        {category.name}
      </h3>
    </div>
  </button>
);

const ProductCard = ({ product, onAdd, hideImage = false }) => (
  <article 
    className="card-product flex flex-col"
    data-testid={`product-${product.id}`}
  >
    {!hideImage && (
      <div className="aspect-video relative overflow-hidden">
        <img 
          src={product.image || "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=400"} 
          alt={product.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-2 right-2 bg-brand-yellow text-brand-navy font-mono font-bold px-2 py-1 text-sm">
          {product.price.toFixed(2)}€
        </div>
      </div>
    )}
    <div className={`p-4 flex-1 flex flex-col ${hideImage ? 'text-center' : ''}`}>
      <div className={`flex items-center justify-between ${hideImage ? 'flex-col gap-2' : ''}`}>
        <h3 className="font-heading text-xl text-brand-navy uppercase">{product.name}</h3>
        {hideImage && (
          <span className="bg-brand-yellow text-brand-navy font-mono font-bold px-3 py-1 text-lg rounded">
            {product.price.toFixed(2)}€
          </span>
        )}
      </div>
      {!hideImage && <p className="text-sm text-brand-navy/70 mt-1 flex-1">{product.description}</p>}
      <button 
        onClick={() => onAdd(product)}
        className="mt-4 btn-primary px-4 py-2 flex items-center justify-center gap-2"
        data-testid={`add-${product.id}`}
      >
        <Plus className="w-4 h-4" />
        <span>Ajouter</span>
      </button>
    </div>
  </article>
);

// ==================== PAGES ====================

const PROMO_STORAGE_KEY = 'friteur_promo_boulettes_seen';

const HomePage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickupMessage, setPickupMessage] = useState(null);
  const [isOrderingForLater, setIsOrderingForLater] = useState(false);
  const [isVacation, setIsVacation] = useState(false);
  const [vacationMessage, setVacationMessage] = useState(null);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [nextSlotInfo, setNextSlotInfo] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, slotsRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/pickup-slots`)
        ]);
        setCategories(catRes.data);
        setPickupMessage(slotsRes.data.message);
        setIsOrderingForLater(slotsRes.data.is_ordering_for_later || false);
        setIsVacation(slotsRes.data.is_vacation || false);
        setVacationMessage(slotsRes.data.vacation_message || null);
        
        // Extraire le premier créneau disponible pour l'affichage
        if (slotsRes.data.slots && slotsRes.data.slots.length > 0) {
          const firstSlot = slotsRes.data.slots[0];
          const lastSlot = slotsRes.data.slots[slotsRes.data.slots.length - 1];
          setNextSlotInfo({
            first: firstSlot,
            last: lastSlot,
            date: slotsRes.data.message ? slotsRes.data.message.match(/\d{2}\/\d{2}/)?.[0] : null
          });
        }
      } catch (e) {
        console.error(e);
        toast.error("Erreur de chargement");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Show promo modal once per user (localStorage check)
  useEffect(() => {
    const hasSeenPromo = localStorage.getItem(PROMO_STORAGE_KEY);
    if (!hasSeenPromo && !loading) {
      // Small delay for better UX - let the page render first
      const timer = setTimeout(() => {
        setShowPromoModal(true);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  const handleClosePromo = () => {
    setShowPromoModal(false);
    localStorage.setItem(PROMO_STORAGE_KEY, 'true');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-red" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream">
      {/* Promo Modal */}
      {showPromoModal && <PromoModal onClose={handleClosePromo} />}
      
      <Header />
      
      {/* Hero */}
      <section className="bg-brand-navy text-white py-12 px-4" data-testid="hero-section">
        <div className="max-w-6xl mx-auto text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_d1916577-2152-462f-ae9a-eb99c2256591/artifacts/9y6urvi0_LOGO_PRINCIPAL_FIRESAUCE%403x.png" 
            alt="FRITEUR" 
            className="h-24 md:h-32 lg:h-40 mx-auto mb-6"
          />
          <p className="font-body text-lg md:text-xl text-brand-cream/80 max-w-xl mx-auto">
            Commandez en ligne, et récupérez votre repas à l'heure souhaitée
          </p>
          
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-brand-yellow" />
              <span>Rue Saint Laurent 30, 4000 Liège</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-brand-yellow" />
              <span>Lun-Ven: 11h-14h / 18h-21h</span>
            </div>
          </div>

          {/* Message de fermeture annuelle */}
          {isVacation && vacationMessage && (
            <div className="mt-6 bg-brand-red border-2 border-white text-white px-6 py-4 rounded-lg max-w-lg mx-auto" data-testid="vacation-banner">
              <p className="text-lg font-bold">{vacationMessage}</p>
            </div>
          )}

          {/* Message de commande différée (si pas en vacances) */}
          {!isVacation && isOrderingForLater && pickupMessage && (
            <div className="mt-6 bg-brand-yellow/20 border-2 border-brand-yellow text-white px-4 py-3 rounded inline-block max-w-md">
              <p className="text-sm">📦 {pickupMessage}</p>
            </div>
          )}

          {/* Prochain créneau disponible + Bouton Commander */}
          {!isVacation && nextSlotInfo && (
            <div className="mt-6 space-y-4">
              <div className="bg-white/10 border border-white/30 rounded-lg px-4 py-3 inline-block" data-testid="next-slot-info">
                <p className="text-sm text-brand-cream/80">Prochain retrait disponible</p>
                <p className="text-lg font-bold text-brand-yellow">
                  {isOrderingForLater && nextSlotInfo.date ? `${nextSlotInfo.date} à partir de ` : ''}{nextSlotInfo.first}
                </p>
              </div>
              
              <div>
                <button
                  onClick={() => {
                    const menuSection = document.querySelector('[data-testid="categories-section"]');
                    if (menuSection) {
                      menuSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="btn-primary px-8 py-4 text-lg font-heading uppercase tracking-wide inline-flex items-center gap-2 hover:scale-105 transition-transform"
                  data-testid="cta-commander"
                >
                  <ShoppingCart className="w-5 h-5" />
                  Commander maintenant
                </button>
              </div>
            </div>
          )}

          {/* Bouton Commander si pas de créneau différé */}
          {!isVacation && !nextSlotInfo && (
            <div className="mt-6">
              <button
                onClick={() => {
                  const menuSection = document.querySelector('[data-testid="categories-section"]');
                  if (menuSection) {
                    menuSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="btn-primary px-8 py-4 text-lg font-heading uppercase tracking-wide inline-flex items-center gap-2 hover:scale-105 transition-transform"
                data-testid="cta-commander"
              >
                <ShoppingCart className="w-5 h-5" />
                Commander maintenant
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Vacation Notice - Full width banner */}
      {isVacation && (
        <section className="bg-brand-red text-white py-6 px-4" data-testid="vacation-section">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl uppercase mb-3">Fermeture Annuelle</h2>
            <p className="text-lg">Du 18 juillet au 30 août 2026</p>
            <p className="text-brand-yellow font-bold mt-2">Nous vous retrouvons le lundi 31 août 2026 !</p>
            <p className="mt-4 text-sm text-white/80">Merci pour votre fidélité et bonnes vacances à tous ! 🏖️</p>
          </div>
        </section>
      )}

      {/* Categories Grid */}
      <section className="max-w-6xl mx-auto px-4 py-12" data-testid="categories-section">
        <h2 className="font-heading text-3xl md:text-4xl text-brand-navy uppercase mb-4">
          Notre Menu
        </h2>
        <p className="text-brand-navy/60 text-sm mb-8 italic">
          Les photos affichées sont strictement les photos de la réalité, pas de publicité mensongère chez Friteur
        </p>
        
        {/* Si en vacances, désactiver les clics sur les catégories */}
        {isVacation ? (
          <div className="opacity-50 pointer-events-none">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {categories.map(cat => (
                <CategoryCard 
                  key={cat.id} 
                  category={cat} 
                  onClick={() => {}}
                />
              ))}
            </div>
            <p className="text-center text-brand-navy mt-8 font-bold">
              Les commandes reprendront le 31 août 2026
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {categories.map(cat => (
              <CategoryCard 
                key={cat.id} 
                category={cat} 
                onClick={() => navigate(`/menu/${cat.slug}`)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Info */}
      <section className="bg-brand-navy text-white py-12 px-4">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-brand-red rounded-full flex items-center justify-center mx-auto mb-4">
              <ShoppingCart className="w-8 h-8" />
            </div>
            <h3 className="font-heading text-xl uppercase mb-2">Commandez</h3>
            <p className="text-brand-cream/70">Choisissez vos produits et personnalisez-les</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-brand-yellow rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-brand-navy" />
            </div>
            <h3 className="font-heading text-xl uppercase mb-2">Choisissez l'heure</h3>
            <p className="text-brand-cream/70">Sélectionnez un créneau de retrait</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-brand-blue rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8" />
            </div>
            <h3 className="font-heading text-xl uppercase mb-2">Récupérez</h3>
            <p className="text-brand-cream/70">Venez chercher votre commande prête</p>
          </div>
        </div>
      </section>
    </div>
  );
};

const MenuPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const catRes = await axios.get(`${API}/categories/${slug}`);
        setCategory(catRes.data);
        
        const prodRes = await axios.get(`${API}/products/category/${catRes.data.id}`);
        setProducts(prodRes.data);
      } catch (e) {
        console.error(e);
        toast.error("Catégorie non trouvée");
        navigate("/");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug, navigate]);

  const handleAddProduct = (product) => {
    if (product.options && product.options.length > 0) {
      setSelectedProduct(product);
    } else {
      addToCart({
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        unit_price: product.price,
        options: [],
        total_price: product.price,
        tva_rate: product.tva_rate
      });
    }
  };

  const handleAddWithOptions = (product, selectedOptions, totalPrice) => {
    addToCart({
      product_id: product.id,
      product_name: product.name,
      quantity: 1,
      unit_price: totalPrice,
      options: selectedOptions,
      total_price: totalPrice,
      tva_rate: product.tva_rate
    });
    setSelectedProduct(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-red" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream">
      <Header showBack />
      
      {/* Category Header */}
      <section 
        className="relative h-48 md:h-64 bg-cover bg-center"
        style={{ backgroundImage: `url(${category?.image})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="max-w-6xl mx-auto">
            <h1 className="font-heading text-4xl md:text-5xl text-white uppercase tracking-wide">
              {category?.name}
            </h1>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="max-w-6xl mx-auto px-4 py-8" data-testid="products-section">
        {products.length === 0 ? (
          <p className="text-center text-brand-navy/60 py-12">Aucun produit dans cette catégorie</p>
        ) : (
          <div className={`grid gap-6 ${slug === 'sauces' ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
            {products.map(product => (
              <ProductCard 
                key={product.id} 
                product={product} 
                onAdd={handleAddProduct}
                hideImage={slug === 'sauces'}
              />
            ))}
          </div>
        )}
      </section>

      {/* Product Options Modal */}
      {selectedProduct && (
        <ProductOptionsModal 
          product={selectedProduct} 
          onClose={() => setSelectedProduct(null)}
          onAdd={handleAddWithOptions}
        />
      )}
    </div>
  );
};

const ProductOptionsModal = ({ product, onClose, onAdd }) => {
  const [selectedOptions, setSelectedOptions] = useState({});
  const [quantity, setQuantity] = useState(1);

  const calculateTotal = () => {
    let total = product.price;
    Object.values(selectedOptions).flat().forEach(opt => {
      if (opt.price_modifier) {
        total += opt.price_modifier;
      }
    });
    return total * quantity;
  };

  const handleOptionChange = (optionId, choice, maxSelections) => {
    setSelectedOptions(prev => {
      const current = prev[optionId] || [];
      const exists = current.find(c => c.name === choice.name);
      
      if (exists) {
        return { ...prev, [optionId]: current.filter(c => c.name !== choice.name) };
      }
      
      if (maxSelections === 1) {
        return { ...prev, [optionId]: [choice] };
      }
      
      if (current.length < maxSelections) {
        return { ...prev, [optionId]: [...current, choice] };
      }
      
      return prev;
    });
  };

  const handleSubmit = () => {
    const formattedOptions = Object.entries(selectedOptions).map(([optId, choices]) => {
      const option = product.options.find(o => o.id === optId);
      return {
        option_name: option?.name,
        choices: choices.map(c => c.name)
      };
    });
    onAdd(product, formattedOptions, calculateTotal() / quantity);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" onClick={onClose}>
      <div 
        className="bg-brand-cream w-full sm:max-w-lg sm:rounded-lg border-2 border-brand-navy shadow-hard max-h-[80vh] overflow-auto"
        onClick={e => e.stopPropagation()}
        data-testid="product-options-modal"
      >
        <div className="sticky top-0 bg-brand-cream border-b-2 border-brand-navy p-4 flex items-center justify-between">
          <h2 className="font-heading text-2xl text-brand-navy uppercase">{product.name}</h2>
          <button onClick={onClose} className="p-2" data-testid="close-modal">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {product.options.map(option => (
            <div key={option.id} className="space-y-3">
              <h3 className="font-body font-bold text-brand-navy uppercase text-sm">
                {option.name}
                {option.required && <span className="text-brand-red ml-1">*</span>}
                {option.max_selections > 1 && <span className="text-brand-navy/50 ml-2">(max {option.max_selections})</span>}
              </h3>
              <div className="space-y-2">
                {option.choices.map(choice => {
                  const isSelected = (selectedOptions[option.id] || []).find(c => c.name === choice.name);
                  return (
                    <button
                      key={choice.name}
                      onClick={() => handleOptionChange(option.id, choice, option.max_selections)}
                      className={`w-full flex items-center justify-between p-3 border-2 transition-all ${
                        isSelected 
                          ? 'border-brand-red bg-brand-red/10' 
                          : 'border-brand-navy/20 hover:border-brand-navy'
                      }`}
                      data-testid={`option-${choice.name}`}
                    >
                      <span className="font-body">{choice.name}</span>
                      <span className="font-mono text-sm">
                        {choice.price_modifier > 0 ? `+${choice.price_modifier.toFixed(2)}€` : 'Inclus'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Quantity */}
          <div className="flex items-center justify-between border-t-2 border-dashed border-brand-navy/20 pt-4">
            <span className="font-body font-bold text-brand-navy">Quantité</span>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="w-10 h-10 border-2 border-brand-navy flex items-center justify-center"
                data-testid="decrease-quantity"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="font-mono text-xl w-8 text-center">{quantity}</span>
              <button 
                onClick={() => setQuantity(quantity + 1)}
                className="w-10 h-10 border-2 border-brand-navy flex items-center justify-center"
                data-testid="increase-quantity"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-brand-cream border-t-2 border-brand-navy p-4">
          <button 
            onClick={handleSubmit}
            className="w-full btn-primary py-4 flex items-center justify-center gap-3"
            data-testid="add-to-cart-button"
          >
            <ShoppingCart className="w-5 h-5" />
            <span>Ajouter • {calculateTotal().toFixed(2)}€</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const CartPage = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, cartTotal, clearCart } = useCart();

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-brand-cream">
        <Header showBack />
        <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="empty-cart">
          <ShoppingCart className="w-16 h-16 text-brand-navy/30 mx-auto mb-4" />
          <h1 className="font-heading text-2xl text-brand-navy uppercase mb-2">Panier vide</h1>
          <p className="text-brand-navy/60 mb-8">Ajoutez des produits pour commencer</p>
          <button 
            onClick={() => navigate("/")}
            className="btn-secondary px-6 py-3"
            data-testid="continue-shopping"
          >
            Voir le menu
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream pb-32">
      <Header showBack />
      
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="font-heading text-3xl text-brand-navy uppercase mb-6">Votre Panier</h1>
        
        {/* Cart Items - Receipt Style */}
        <div className="bg-white border-2 border-brand-navy shadow-hard" data-testid="cart-items">
          <div className="p-4 border-b-2 border-dashed border-brand-navy/20">
            <div className="flex items-center justify-between">
              <span className="font-heading text-lg text-brand-navy">FRITEUR</span>
              <span className="font-mono text-xs text-brand-navy/60">{new Date().toLocaleDateString('fr-BE')}</span>
            </div>
          </div>

          <div className="divide-y divide-dashed divide-brand-navy/20">
            {cart.map((item, index) => (
              <div key={index} className="p-4" data-testid={`cart-item-${index}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="font-body font-bold text-brand-navy">{item.product_name}</h3>
                    {item.options && item.options.length > 0 && (
                      <p className="text-xs text-brand-navy/60 mt-1">
                        {item.options.map(o => o.choices?.join(", ")).join(" • ")}
                      </p>
                    )}
                  </div>
                  <span className="font-mono font-bold text-brand-navy">{item.total_price.toFixed(2)}€</span>
                </div>
                
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => updateQuantity(index, item.quantity - 1)}
                      className="w-8 h-8 border border-brand-navy/30 flex items-center justify-center hover:bg-brand-cream"
                      data-testid={`decrease-${index}`}
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="font-mono w-6 text-center">{item.quantity}</span>
                    <button 
                      onClick={() => updateQuantity(index, item.quantity + 1)}
                      className="w-8 h-8 border border-brand-navy/30 flex items-center justify-center hover:bg-brand-cream"
                      data-testid={`increase-${index}`}
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  <button 
                    onClick={() => removeFromCart(index)}
                    className="text-brand-red hover:text-brand-red/70"
                    data-testid={`remove-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="p-4 border-t-2 border-brand-navy bg-brand-cream/50">
            <div className="flex justify-between items-center">
              <span className="font-heading text-xl text-brand-navy uppercase">Total</span>
              <span className="font-mono text-2xl font-bold text-brand-navy">{cartTotal.toFixed(2)}€</span>
            </div>
          </div>
        </div>

        <button 
          onClick={clearCart}
          className="mt-4 text-sm text-brand-navy/60 hover:text-brand-red underline"
          data-testid="clear-cart"
        >
          Vider le panier
        </button>
      </div>

      {/* Fixed Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-brand-navy border-t-2 border-brand-yellow p-4">
        <div className="max-w-2xl mx-auto">
          <button 
            onClick={() => navigate("/checkout")}
            className="w-full btn-primary py-4 text-lg"
            data-testid="checkout-button"
          >
            Commander • {cartTotal.toFixed(2)}€
          </button>
        </div>
      </div>
    </div>
  );
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cart, cartTotal, clearCart } = useCart();
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(true);
  const [pickupMessage, setPickupMessage] = useState(null);
  const [pickupDate, setPickupDate] = useState(null);
  const [isOrderingForLater, setIsOrderingForLater] = useState(false);
  const [isVacation, setIsVacation] = useState(false);
  const [vacationMessage, setVacationMessage] = useState(null);

  const cancelled = searchParams.get("cancelled");

  useEffect(() => {
    if (cancelled) {
      toast.error("Paiement annulé");
    }
  }, [cancelled]);

  useEffect(() => {
    const fetchSlots = async () => {
      try {
        const res = await axios.get(`${API}/pickup-slots`);
        setSlots(res.data.slots);
        setIsOpen(res.data.is_open);
        setPickupMessage(res.data.message);
        setPickupDate(res.data.date);
        setIsOrderingForLater(res.data.is_ordering_for_later || false);
        setIsVacation(res.data.is_vacation || false);
        setVacationMessage(res.data.vacation_message || null);
        if (res.data.slots.length > 0) {
          setSelectedSlot(res.data.slots[0]);
        }
      } catch (e) {
        console.error(e);
        toast.error("Erreur de chargement des créneaux");
      } finally {
        setSlotsLoading(false);
      }
    };
    fetchSlots();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!customerName.trim() || !customerPhone.trim()) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    
    if (!selectedSlot) {
      toast.error("Veuillez sélectionner un créneau");
      return;
    }

    setLoading(true);

    try {
      // Create order with pickup date and time
      const pickupDateTime = pickupDate ? `${pickupDate} ${selectedSlot}` : selectedSlot;
      const orderRes = await axios.post(`${API}/orders`, {
        customer_name: customerName,
        customer_phone: customerPhone,
        items: cart,
        pickup_time: pickupDateTime
      });

      // Create checkout session
      const checkoutRes = await axios.post(`${API}/checkout/session`, null, {
        params: {
          order_id: orderRes.data.id,
          origin_url: window.location.origin
        }
      });

      // Redirect to Stripe
      window.location.href = checkoutRes.data.url;
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la création de la commande");
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    navigate("/");
    return null;
  }

  return (
    <div className="min-h-screen bg-brand-cream pb-8">
      <Header showBack />
      
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="font-heading text-3xl text-brand-navy uppercase mb-6">Finaliser</h1>

        {/* Message de fermeture annuelle */}
        {isVacation ? (
          <div className="bg-brand-red text-white p-8 text-center rounded-lg" data-testid="vacation-checkout-message">
            <h2 className="font-heading text-2xl uppercase mb-4">🏖️ Fermeture Annuelle</h2>
            <p className="text-lg mb-4">{vacationMessage}</p>
            <p className="text-brand-yellow font-bold">Les commandes reprendront le lundi 31 août 2026</p>
            <button
              onClick={() => navigate("/")}
              className="mt-6 btn-primary px-8 py-3"
            >
              Retour à l'accueil
            </button>
          </div>
        ) : !isOpen ? (
          <div className="bg-brand-red/10 border-2 border-brand-red text-brand-red p-6 text-center" data-testid="closed-message">
            <h2 className="font-heading text-xl uppercase mb-2">Fermé</h2>
            <p>Nous sommes actuellement fermés. Revenez pendant nos horaires d'ouverture.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Customer Info */}
            <div className="bg-white border-2 border-brand-navy shadow-hard p-6 space-y-4">
              <h2 className="font-heading text-xl text-brand-navy uppercase">Vos coordonnées</h2>
              
              <div>
                <label className="block font-body font-bold text-brand-navy text-sm uppercase mb-2">
                  Nom complet
                </label>
                <input 
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-brand-navy focus:border-brand-red focus:outline-none font-body"
                  placeholder="Jean Dupont"
                  required
                  data-testid="customer-name"
                />
              </div>

              <div>
                <label className="block font-body font-bold text-brand-navy text-sm uppercase mb-2">
                  Téléphone
                </label>
                <input 
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-brand-navy focus:border-brand-red focus:outline-none font-body"
                  placeholder="+32 4XX XX XX XX"
                  required
                  data-testid="customer-phone"
                />
              </div>
            </div>

            {/* Pickup Time */}
            <div className="bg-white border-2 border-brand-navy shadow-hard p-6">
              <h2 className="font-heading text-xl text-brand-navy uppercase mb-4">Heure de retrait</h2>
              
              {/* Message d'information pour commande différée */}
              {pickupMessage && (
                <div className="bg-brand-yellow/20 border-2 border-brand-yellow text-brand-navy p-4 mb-4 rounded" data-testid="pickup-message">
                  <p className="font-body text-sm">{pickupMessage}</p>
                </div>
              )}
              
              {slotsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-brand-red" />
                </div>
              ) : slots.length === 0 ? (
                <p className="text-brand-navy/60 text-center py-4">Aucun créneau disponible</p>
              ) : (
                <>
                  {isOrderingForLater && pickupDate && (
                    <p className="font-body text-sm text-brand-navy/70 mb-3">
                      📅 Date de retrait : <strong>{new Date(pickupDate).toLocaleDateString('fr-BE', { weekday: 'long', day: 'numeric', month: 'long' })}</strong>
                    </p>
                  )}
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="time-slots">
                    {slots.map(slot => (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => setSelectedSlot(slot)}
                        className={`py-3 px-2 border-2 font-mono text-sm transition-all ${
                          selectedSlot === slot
                            ? 'border-brand-red bg-brand-red text-white'
                            : 'border-brand-navy/30 hover:border-brand-navy'
                        }`}
                        data-testid={`slot-${slot}`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Summary */}
            <div className="bg-white border-2 border-brand-navy shadow-hard p-6">
              <h2 className="font-heading text-xl text-brand-navy uppercase mb-4">Récapitulatif</h2>
              
              <div className="space-y-2 font-mono text-sm border-b-2 border-dashed border-brand-navy/20 pb-4 mb-4">
                {cart.map((item, i) => (
                  <div key={i} className="flex justify-between">
                    <span>{item.quantity}x {item.product_name}</span>
                    <span>{item.total_price.toFixed(2)}€</span>
                  </div>
                ))}
              </div>

              <div className="flex justify-between font-heading text-xl text-brand-navy">
                <span>TOTAL</span>
                <span>{cartTotal.toFixed(2)}€</span>
              </div>
            </div>

            {/* Submit */}
            <button 
              type="submit"
              disabled={loading || slots.length === 0}
              className="w-full btn-primary py-4 text-lg flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="pay-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Redirection...</span>
                </>
              ) : (
                <>
                  <span>Payer {cartTotal.toFixed(2)}€</span>
                </>
              )}
            </button>

            <p className="text-center text-xs text-brand-navy/50">
              Paiement sécurisé par Mollie • CB, Bancontact, Apple Pay, Google Pay
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

const OrderConfirmationPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState("checking");

  // Mollie returns payment_id in URL
  const paymentId = searchParams.get("payment_id");

  const pollPaymentStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus("timeout");
      return;
    }

    try {
      const res = await axios.get(`${API}/checkout/status/${paymentId}`);
      
      if (res.data.payment_status === "paid") {
        setPaymentStatus("paid");
        clearCart();
        // Refresh order data
        const orderRes = await axios.get(`${API}/orders/${id}`);
        setOrder(orderRes.data);
        return;
      } else if (res.data.payment_status === "expired" || res.data.payment_status === "failed" || res.data.payment_status === "canceled") {
        setPaymentStatus(res.data.payment_status);
        return;
      }

      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (e) {
      console.error(e);
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    }
  }, [paymentId, id, clearCart]);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await axios.get(`${API}/orders/${id}`);
        setOrder(res.data);
        
        if (paymentId) {
          pollPaymentStatus();
        } else {
          setPaymentStatus(res.data.status === "paid" ? "paid" : "unknown");
        }
      } catch (e) {
        console.error(e);
        toast.error("Commande non trouvée");
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id, paymentId, pollPaymentStatus]);

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-red" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-brand-cream">
        <Header />
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <h1 className="font-heading text-2xl text-brand-navy uppercase">Commande non trouvée</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream">
      <Header />
      
      <div className="max-w-md mx-auto px-4 py-8" data-testid="order-confirmation">
        {paymentStatus === "checking" && (
          <div className="bg-brand-yellow/20 border-2 border-brand-yellow p-6 text-center mb-6">
            <Loader2 className="w-8 h-8 animate-spin text-brand-yellow mx-auto mb-2" />
            <p className="font-body text-brand-navy">Vérification du paiement...</p>
          </div>
        )}

        {paymentStatus === "paid" && (
          <div className="bg-green-100 border-2 border-green-600 p-6 text-center mb-6" data-testid="payment-success">
            <Check className="w-12 h-12 text-green-600 mx-auto mb-2" />
            <h2 className="font-heading text-2xl text-green-700 uppercase">Paiement confirmé!</h2>
            <p className="text-green-600 mt-1">Votre commande est en préparation</p>
          </div>
        )}

        {paymentStatus === "expired" && (
          <div className="bg-brand-red/10 border-2 border-brand-red p-6 text-center mb-6">
            <X className="w-12 h-12 text-brand-red mx-auto mb-2" />
            <h2 className="font-heading text-2xl text-brand-red uppercase">Session expirée</h2>
            <p className="text-brand-red/70 mt-1">Veuillez recommencer votre commande</p>
          </div>
        )}

        {/* Order Receipt */}
        <div className="bg-white border-2 border-brand-navy shadow-hard">
          <div className="p-4 border-b-2 border-brand-navy bg-brand-navy text-white">
            <div className="flex items-center justify-between">
              <span className="font-heading text-xl">FRITEUR</span>
              <span className="font-mono text-xs">#{order.order_number}</span>
            </div>
          </div>

          <div className="p-4 border-b-2 border-dashed border-brand-navy/20">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-brand-navy/60 uppercase text-xs">Client</p>
                <p className="font-bold text-brand-navy">{order.customer_name}</p>
              </div>
              <div>
                <p className="text-brand-navy/60 uppercase text-xs">Téléphone</p>
                <p className="font-bold text-brand-navy">{order.customer_phone}</p>
              </div>
              <div>
                <p className="text-brand-navy/60 uppercase text-xs">Retrait</p>
                <p className="font-bold text-brand-navy text-lg">{order.pickup_time}</p>
              </div>
              <div>
                <p className="text-brand-navy/60 uppercase text-xs">Statut</p>
                <p className={`font-bold uppercase ${
                  order.status === 'paid' ? 'text-green-600' :
                  order.status === 'preparing' ? 'text-brand-yellow' :
                  order.status === 'ready' ? 'text-brand-blue' :
                  'text-brand-navy'
                }`}>
                  {order.status === 'paid' ? 'Payée' :
                   order.status === 'preparing' ? 'En préparation' :
                   order.status === 'ready' ? 'Prête' :
                   order.status === 'completed' ? 'Terminée' :
                   order.status}
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 border-b-2 border-dashed border-brand-navy/20">
            <p className="text-brand-navy/60 uppercase text-xs mb-2">Articles</p>
            <div className="space-y-2 font-mono text-sm">
              {order.items.map((item, i) => (
                <div key={i} className="flex justify-between">
                  <span>{item.quantity}x {item.product_name}</span>
                  <span>{item.total_price.toFixed(2)}€</span>
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 bg-brand-cream/50">
            <div className="flex justify-between font-heading text-xl text-brand-navy">
              <span>TOTAL</span>
              <span>{order.total.toFixed(2)}€</span>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-brand-navy/60 text-sm mb-4">
            <MapPin className="w-4 h-4 inline mr-1" />
            Rue Saint Laurent 30, 4000 Liège
          </p>
          <Link 
            to="/"
            className="btn-secondary px-6 py-3 inline-block"
            data-testid="back-home"
          >
            Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );
};

// ==================== ADMIN PAGES ====================

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (token) {
      navigate("/admin/dashboard");
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem("admin_token", res.data.token);
      toast.success("Connexion réussie");
      navigate("/admin/dashboard");
    } catch (e) {
      console.error(e);
      toast.error("Identifiants incorrects");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-navy flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_d1916577-2152-462f-ae9a-eb99c2256591/artifacts/9y6urvi0_LOGO_PRINCIPAL_FIRESAUCE%403x.png" 
            alt="FRITEUR" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="font-heading text-3xl text-white uppercase">Admin</h1>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border-2 border-brand-yellow shadow-hard p-6 space-y-4" data-testid="admin-login-form">
          <div>
            <label className="block font-body font-bold text-brand-navy text-sm uppercase mb-2">
              Email
            </label>
            <input 
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border-2 border-brand-navy focus:border-brand-red focus:outline-none font-body"
              placeholder="admin@friteur.be"
              required
              data-testid="admin-email"
            />
          </div>

          <div>
            <label className="block font-body font-bold text-brand-navy text-sm uppercase mb-2">
              Mot de passe
            </label>
            <input 
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border-2 border-brand-navy focus:border-brand-red focus:outline-none font-body"
              placeholder="••••••••"
              required
              data-testid="admin-password"
            />
          </div>

          <button 
            type="submit"
            disabled={loading}
            className="w-full btn-primary py-3 flex items-center justify-center gap-2"
            data-testid="admin-login-button"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <User className="w-5 h-5" />}
            <span>Connexion</span>
          </button>
        </form>

        <p className="text-center text-brand-cream/60 text-sm mt-4">
          Identifiants par défaut: admin@friteur.be / friteur2024
        </p>
      </div>
    </div>
  );
};

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-brand-navy">
      {/* Admin Header */}
      <header className="bg-brand-navy border-b border-brand-navy/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setMenuOpen(!menuOpen)} 
              className="md:hidden text-white p-2"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
            </button>
            <Link to="/admin/dashboard" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_d1916577-2152-462f-ae9a-eb99c2256591/artifacts/9y6urvi0_LOGO_PRINCIPAL_FIRESAUCE%403x.png" 
                alt="FRITEUR" 
                className="h-8"
              />
              <span className="font-heading text-white text-xl hidden sm:block">ADMIN</span>
            </Link>
          </div>

          <nav className="hidden md:flex items-center gap-6">
            <Link to="/admin/dashboard" className="text-white/80 hover:text-white font-body uppercase text-sm">Dashboard</Link>
            <Link to="/admin/orders" className="text-white/80 hover:text-white font-body uppercase text-sm">Commandes</Link>
            <Link to="/admin/menu" className="text-white/80 hover:text-white font-body uppercase text-sm">Menu</Link>
            <Link to="/admin/reports" className="text-white/80 hover:text-white font-body uppercase text-sm">Rapports</Link>
          </nav>

          <button 
            onClick={handleLogout}
            className="text-white/80 hover:text-white p-2"
            data-testid="admin-logout"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <nav className="md:hidden bg-brand-navy/95 border-t border-brand-yellow/20 p-4">
            <Link to="/admin/dashboard" className="block py-2 text-white font-body uppercase" onClick={() => setMenuOpen(false)}>Dashboard</Link>
            <Link to="/admin/orders" className="block py-2 text-white font-body uppercase" onClick={() => setMenuOpen(false)}>Commandes</Link>
            <Link to="/admin/menu" className="block py-2 text-white font-body uppercase" onClick={() => setMenuOpen(false)}>Menu</Link>
            <Link to="/admin/reports" className="block py-2 text-white font-body uppercase" onClick={() => setMenuOpen(false)}>Rapports</Link>
          </nav>
        )}
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {children}
      </main>
    </div>
  );
};

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ today: 0, pending: 0, total: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }

    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/admin/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const orders = res.data;
        const today = new Date().toISOString().split('T')[0];
        
        setStats({
          today: orders.filter(o => o.created_at?.startsWith(today)).length,
          pending: orders.filter(o => ['paid', 'preparing'].includes(o.status)).length,
          total: orders.filter(o => o.status !== 'cancelled').reduce((sum, o) => sum + o.total, 0)
        });
        
        setRecentOrders(orders.slice(0, 5));
      } catch (e) {
        console.error(e);
        if (e.response?.status === 401) {
          localStorage.removeItem("admin_token");
          navigate("/admin");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-brand-yellow" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <h1 className="font-heading text-3xl text-white uppercase mb-8">Dashboard</h1>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-brand-red p-6 border-2 border-brand-yellow" data-testid="stat-today">
          <p className="text-white/80 uppercase text-sm">Commandes aujourd'hui</p>
          <p className="font-heading text-4xl text-white">{stats.today}</p>
        </div>
        <div className="bg-brand-yellow p-6 border-2 border-brand-navy" data-testid="stat-pending">
          <p className="text-brand-navy/80 uppercase text-sm">En attente</p>
          <p className="font-heading text-4xl text-brand-navy">{stats.pending}</p>
        </div>
        <div className="bg-brand-blue p-6 border-2 border-brand-navy" data-testid="stat-total">
          <p className="text-white/80 uppercase text-sm">CA Total</p>
          <p className="font-heading text-4xl text-white">{stats.total.toFixed(2)}€</p>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white/10 border-2 border-brand-yellow/20 p-6">
        <h2 className="font-heading text-xl text-white uppercase mb-4">Dernières commandes</h2>
        
        {recentOrders.length === 0 ? (
          <p className="text-white/60">Aucune commande</p>
        ) : (
          <div className="space-y-2">
            {recentOrders.map(order => (
              <div key={order.id} className="flex items-center justify-between bg-white/5 p-3">
                <div>
                  <span className="font-mono text-brand-yellow text-sm">#{order.order_number}</span>
                  <span className="text-white ml-3">{order.customer_name}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-white/60 text-sm">{order.pickup_time}</span>
                  <span className={`uppercase text-xs font-bold px-2 py-1 ${
                    order.status === 'paid' ? 'bg-green-600 text-white' :
                    order.status === 'preparing' ? 'bg-brand-yellow text-brand-navy' :
                    order.status === 'ready' ? 'bg-brand-blue text-white' :
                    'bg-white/20 text-white'
                  }`}>
                    {order.status}
                  </span>
                  <span className="font-mono text-white">{order.total.toFixed(2)}€</span>
                </div>
              </div>
            ))}
          </div>
        )}

        <Link 
          to="/admin/orders" 
          className="inline-block mt-4 text-brand-yellow hover:text-brand-yellow/80 uppercase text-sm"
        >
          Voir toutes les commandes →
        </Link>
      </div>

      {/* Seed Data Button */}
      <div className="mt-8">
        <button
          onClick={async () => {
            try {
              const token = localStorage.getItem("admin_token");
              await axios.post(`${API}/admin/seed`, {}, {
                headers: { Authorization: `Bearer ${token}` }
              });
              toast.success("Données de démonstration créées");
            } catch (e) {
              toast.error("Erreur");
            }
          }}
          className="btn-secondary px-4 py-2 text-sm"
          data-testid="seed-data"
        >
          Créer données démo
        </button>
      </div>
    </AdminLayout>
  );
};

const AdminOrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [newOrderAlert, setNewOrderAlert] = useState(false);
  const lastOrderCountRef = useRef(0);
  const isFirstLoadRef = useRef(true);

  const fetchOrders = useCallback(async (showAlert = true) => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }

    try {
      const res = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newOrders = res.data;
      const paidOrders = newOrders.filter(o => o.status === 'paid').length;
      
      // Vérifier s'il y a de nouvelles commandes payées
      if (!isFirstLoadRef.current && showAlert && paidOrders > lastOrderCountRef.current) {
        const newCount = paidOrders - lastOrderCountRef.current;
        // Jouer le son
        if (soundEnabled) {
          playOrderSound();
        }
        // Afficher notification
        setNewOrderAlert(true);
        toast.success(`🔔 ${newCount} nouvelle${newCount > 1 ? 's' : ''} commande${newCount > 1 ? 's' : ''} !`, {
          duration: 10000,
          style: { background: '#E50D1E', color: 'white', fontSize: '18px', padding: '20px' }
        });
        // Cacher l'alerte après 5 secondes
        setTimeout(() => setNewOrderAlert(false), 5000);
      }
      
      lastOrderCountRef.current = paidOrders;
      isFirstLoadRef.current = false;
      setOrders(newOrders);
    } catch (e) {
      console.error(e);
      if (e.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, soundEnabled]);

  useEffect(() => {
    fetchOrders(false); // Premier chargement sans alerte
    
    // Polling toutes les 15 secondes
    const interval = setInterval(() => {
      fetchOrders(true);
    }, 15000);
    
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const updateStatus = async (orderId, newStatus) => {
    const token = localStorage.getItem("admin_token");
    try {
      await axios.patch(`${API}/admin/orders/${orderId}?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Statut mis à jour");
      fetchOrders(false);
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const testSound = () => {
    playOrderSound();
    toast.info("Test du son d'alerte");
  };

  const filteredOrders = filter === "all" ? orders : orders.filter(o => o.status === filter);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-brand-yellow" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Alerte nouvelle commande */}
      {newOrderAlert && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-pulse">
          <div className="bg-brand-red text-white px-8 py-4 rounded-lg shadow-lg border-4 border-brand-yellow flex items-center gap-3">
            <Bell className="w-8 h-8 animate-bounce" />
            <span className="font-heading text-2xl">NOUVELLE COMMANDE !</span>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <h1 className="font-heading text-3xl text-white uppercase">Commandes</h1>
          
          {/* Contrôles son */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`p-2 rounded ${soundEnabled ? 'bg-brand-yellow text-brand-navy' : 'bg-white/20 text-white/50'}`}
              title={soundEnabled ? "Son activé" : "Son désactivé"}
              data-testid="toggle-sound"
            >
              <Volume2 className="w-5 h-5" />
            </button>
            <button
              onClick={testSound}
              className="px-3 py-1 text-xs bg-white/10 text-white hover:bg-white/20 rounded"
              data-testid="test-sound"
            >
              Tester
            </button>
          </div>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          {["all", "paid", "preparing", "ready", "completed"].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-3 py-1 text-sm uppercase font-body ${
                filter === status 
                  ? 'bg-brand-yellow text-brand-navy' 
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              {status === "all" ? "Toutes" : status}
            </button>
          ))}
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <p className="text-white/60 text-center py-8">Aucune commande</p>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map(order => (
            <div key={order.id} className="bg-white/10 border-2 border-brand-yellow/20 p-4" data-testid={`order-${order.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <div>
                  <span className="font-mono text-brand-yellow">#{order.order_number}</span>
                  <span className="text-white ml-3 font-bold">{order.customer_name}</span>
                  <span className="text-white/60 ml-3">{order.customer_phone}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-brand-yellow" />
                  <span className="text-white font-mono">{order.pickup_time}</span>
                  <span className="font-mono text-white font-bold">{order.total.toFixed(2)}€</span>
                </div>
              </div>

              <div className="text-white/70 text-sm mb-4 font-mono">
                {order.items.map((item, i) => (
                  <span key={i}>
                    {item.quantity}x {item.product_name}
                    {i < order.items.length - 1 && " • "}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap gap-2">
                {["paid", "preparing", "ready", "completed", "cancelled"].map(status => (
                  <button
                    key={status}
                    onClick={() => updateStatus(order.id, status)}
                    className={`px-3 py-1 text-xs uppercase ${
                      order.status === status
                        ? status === 'cancelled' ? 'bg-brand-red text-white' :
                          status === 'paid' ? 'bg-green-600 text-white' :
                          status === 'preparing' ? 'bg-brand-yellow text-brand-navy' :
                          status === 'ready' ? 'bg-brand-blue text-white' :
                          'bg-white/30 text-white'
                        : 'bg-white/10 text-white/60 hover:bg-white/20'
                    }`}
                  >
                    {status === 'paid' ? 'Payée' :
                     status === 'preparing' ? 'En préparation' :
                     status === 'ready' ? 'Prête' :
                     status === 'completed' ? 'Terminée' :
                     status === 'cancelled' ? 'Annulée' : status}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

const AdminMenuPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("products");

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }

    const fetchData = async () => {
      try {
        const [catRes, prodRes] = await Promise.all([
          axios.get(`${API}/admin/categories`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setCategories(catRes.data);
        setProducts(prodRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-brand-yellow" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <h1 className="font-heading text-3xl text-white uppercase mb-8">Gestion du Menu</h1>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab("products")}
          className={`px-4 py-2 uppercase font-body ${
            activeTab === "products" ? 'bg-brand-yellow text-brand-navy' : 'bg-white/10 text-white'
          }`}
        >
          Produits ({products.length})
        </button>
        <button
          onClick={() => setActiveTab("categories")}
          className={`px-4 py-2 uppercase font-body ${
            activeTab === "categories" ? 'bg-brand-yellow text-brand-navy' : 'bg-white/10 text-white'
          }`}
        >
          Catégories ({categories.length})
        </button>
      </div>

      {activeTab === "products" && (
        <div className="grid gap-4">
          {products.map(product => (
            <div key={product.id} className="bg-white/10 border-2 border-brand-yellow/20 p-4 flex items-center gap-4">
              {product.image && (
                <img src={product.image} alt={product.name} className="w-16 h-16 object-cover" />
              )}
              <div className="flex-1">
                <h3 className="text-white font-bold">{product.name}</h3>
                <p className="text-white/60 text-sm">{product.description}</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-brand-yellow text-lg">{product.price.toFixed(2)}€</p>
                <p className="text-white/40 text-xs">TVA {product.tva_rate}%</p>
              </div>
              <span className={`px-2 py-1 text-xs uppercase ${product.active ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
                {product.active ? 'Actif' : 'Inactif'}
              </span>
            </div>
          ))}
        </div>
      )}

      {activeTab === "categories" && (
        <div className="grid gap-4">
          {categories.map(cat => (
            <div key={cat.id} className="bg-white/10 border-2 border-brand-yellow/20 p-4 flex items-center gap-4">
              {cat.image && (
                <img src={cat.image} alt={cat.name} className="w-16 h-16 object-cover" />
              )}
              <div className="flex-1">
                <h3 className="text-white font-bold">{cat.name}</h3>
                <p className="text-white/60 text-sm">Slug: {cat.slug}</p>
              </div>
              <span className="text-white/40">Ordre: {cat.order}</span>
            </div>
          ))}
        </div>
      )}

      <p className="text-white/40 text-sm mt-8">
        Note: L'édition des produits sera disponible dans une prochaine version. Utilisez le bouton "Créer données démo" dans le Dashboard pour peupler le menu.
      </p>
    </AdminLayout>
  );
};

const AdminReportsPage = () => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateReport = async () => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reports`, {
        params: { start_date: startDate, end_date: endDate },
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(res.data);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la génération du rapport");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <h1 className="font-heading text-3xl text-white uppercase mb-8">Rapports</h1>

      <div className="bg-white/10 border-2 border-brand-yellow/20 p-6 mb-8">
        <h2 className="font-heading text-xl text-white uppercase mb-4">Rapport HTVA</h2>
        
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div>
            <label className="block text-white/60 text-sm mb-1">Date début</label>
            <input 
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-4 py-2 bg-white/10 border border-brand-yellow/30 text-white"
              data-testid="start-date"
            />
          </div>
          <div>
            <label className="block text-white/60 text-sm mb-1">Date fin</label>
            <input 
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-4 py-2 bg-white/10 border border-brand-yellow/30 text-white"
              data-testid="end-date"
            />
          </div>
          <div className="flex items-end">
            <button 
              onClick={generateReport}
              disabled={loading}
              className="btn-primary px-6 py-2"
              data-testid="generate-report"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Générer"}
            </button>
          </div>
        </div>

        {report && (
          <div className="mt-6 bg-white/5 p-6" data-testid="report-results">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div>
                <p className="text-white/60 text-sm">Commandes</p>
                <p className="font-heading text-2xl text-white">{report.order_count}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm">Total TTC</p>
                <p className="font-heading text-2xl text-brand-yellow">{report.total_ttc.toFixed(2)}€</p>
              </div>
              <div>
                <p className="text-white/60 text-sm">Total HTVA</p>
                <p className="font-heading text-2xl text-white">{report.total_htva.toFixed(2)}€</p>
              </div>
              <div>
                <p className="text-white/60 text-sm">TVA</p>
                <p className="font-heading text-2xl text-brand-red">{report.total_tva.toFixed(2)}€</p>
              </div>
            </div>

            {Object.keys(report.tva_breakdown).length > 0 && (
              <div>
                <p className="text-white/60 text-sm mb-2">Détail TVA</p>
                <div className="font-mono text-white text-sm space-y-1">
                  {Object.entries(report.tva_breakdown).map(([rate, amount]) => (
                    <div key={rate} className="flex justify-between">
                      <span>TVA {rate}</span>
                      <span>{amount.toFixed(2)}€</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

// ==================== KITCHEN PAGE (Auto Print) ====================

const KitchenPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [printedOrders, setPrintedOrders] = useState(() => {
    const saved = localStorage.getItem("friteur_printed_orders");
    return saved ? JSON.parse(saved) : [];
  });
  const [printerIP, setPrinterIP] = useState(() => {
    return localStorage.getItem("friteur_printer_ip") || "192.168.129.131";
  });
  const [autoPrint, setAutoPrint] = useState(true);
  const [isConnected, setIsConnected] = useState(true);
  const lastCheckRef = useRef(new Date().toISOString());

  // Sauvegarder les commandes imprimées
  useEffect(() => {
    localStorage.setItem("friteur_printed_orders", JSON.stringify(printedOrders));
  }, [printedOrders]);

  // Sauvegarder l'IP
  useEffect(() => {
    localStorage.setItem("friteur_printer_ip", printerIP);
  }, [printerIP]);

  // Générer le ticket ESC/POS
  const generateTicket = (order) => {
    const ESC = '\x1B';
    const GS = '\x1D';
    
    let ticket = '';
    
    // Initialiser + centrer
    ticket += ESC + '@'; // Reset
    ticket += ESC + 'a' + '\x01'; // Centrer
    
    // Logo texte en gros
    ticket += GS + '!' + '\x11'; // Double hauteur + largeur
    ticket += 'FRITEUR\n';
    ticket += GS + '!' + '\x00'; // Normal
    ticket += '\n';
    
    // Numéro de commande
    ticket += GS + '!' + '\x11'; // Gros
    ticket += `#${order.order_number}\n`;
    ticket += GS + '!' + '\x00'; // Normal
    ticket += '\n';
    
    // Heure de retrait
    ticket += ESC + 'E' + '\x01'; // Gras
    ticket += `RETRAIT: ${order.pickup_time}\n`;
    ticket += ESC + 'E' + '\x00'; // Normal
    ticket += `Client: ${order.customer_name}\n`;
    ticket += `Tel: ${order.customer_phone}\n`;
    ticket += '\n';
    
    // Ligne séparatrice
    ticket += '--------------------------------\n';
    
    // Articles
    ticket += ESC + 'a' + '\x00'; // Aligner à gauche
    order.items.forEach(item => {
      ticket += ESC + 'E' + '\x01'; // Gras
      ticket += `${item.quantity}x ${item.product_name}\n`;
      ticket += ESC + 'E' + '\x00'; // Normal
      
      // Options
      if (item.options && item.options.length > 0) {
        item.options.forEach(opt => {
          if (opt.choices && opt.choices.length > 0) {
            ticket += `   -> ${opt.choices.join(', ')}\n`;
          }
        });
      }
    });
    
    // Ligne séparatrice
    ticket += '--------------------------------\n';
    
    // Total
    ticket += ESC + 'a' + '\x02'; // Aligner à droite
    ticket += ESC + 'E' + '\x01'; // Gras
    ticket += `TOTAL: ${order.total.toFixed(2)} EUR\n`;
    ticket += ESC + 'E' + '\x00'; // Normal
    
    // Espace et coupe
    ticket += '\n\n\n';
    ticket += GS + 'V' + '\x00'; // Coupe papier
    
    return ticket;
  };

  // Imprimer via RawBT
  const printOrder = (order) => {
    const ticket = generateTicket(order);
    const base64Ticket = btoa(unescape(encodeURIComponent(ticket)));
    
    // Ouvrir RawBT avec les données
    const rawbtUrl = `rawbt:base64,${base64Ticket}`;
    
    // Créer un lien invisible et cliquer dessus
    const link = document.createElement('a');
    link.href = rawbtUrl;
    link.click();
    
    // Marquer comme imprimé
    setPrintedOrders(prev => [...prev, order.id]);
    
    // Son de confirmation
    playOrderSound();
    
    toast.success(`Ticket imprimé: #${order.order_number}`);
  };

  // Récupérer les commandes
  const fetchOrders = useCallback(async () => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }

    try {
      const res = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setIsConnected(true);
      const allOrders = res.data;
      
      // Filtrer les commandes payées non imprimées
      const paidOrders = allOrders.filter(o => 
        o.status === 'paid' && !printedOrders.includes(o.id)
      );
      
      // Auto-impression des nouvelles commandes
      if (autoPrint && paidOrders.length > 0) {
        paidOrders.forEach(order => {
          printOrder(order);
        });
      }
      
      // Garder toutes les commandes du jour pour affichage
      const todayOrders = allOrders.filter(o => 
        ['paid', 'preparing', 'ready'].includes(o.status)
      );
      setOrders(todayOrders);
      
    } catch (e) {
      console.error(e);
      setIsConnected(false);
      if (e.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
    }
  }, [navigate, printedOrders, autoPrint]);

  // Polling toutes les 10 secondes
  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 10000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  // Réimprimer manuellement
  const reprintOrder = (order) => {
    printOrder(order);
  };

  // Effacer l'historique d'impression
  const clearPrintHistory = () => {
    setPrintedOrders([]);
    toast.info("Historique d'impression effacé");
  };

  return (
    <div className="min-h-screen bg-brand-navy p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <img 
            src="https://customer-assets.emergentagent.com/job_d1916577-2152-462f-ae9a-eb99c2256591/artifacts/9y6urvi0_LOGO_PRINCIPAL_FIRESAUCE%403x.png" 
            alt="FRITEUR" 
            className="h-10"
          />
          <h1 className="font-heading text-2xl text-white">CUISINE</h1>
          
          {/* Status connexion */}
          <div className={`px-3 py-1 rounded-full text-sm ${isConnected ? 'bg-green-600' : 'bg-red-600'} text-white`}>
            {isConnected ? '● Connecté' : '● Déconnecté'}
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Toggle auto-print */}
          <button
            onClick={() => setAutoPrint(!autoPrint)}
            className={`px-4 py-2 rounded font-body ${autoPrint ? 'bg-green-600 text-white' : 'bg-white/20 text-white'}`}
          >
            {autoPrint ? '🖨️ Auto ON' : '🖨️ Auto OFF'}
          </button>
          
          <Link to="/admin/orders" className="text-white/60 hover:text-white">
            ← Retour admin
          </Link>
        </div>
      </div>

      {/* Config imprimante */}
      <div className="bg-white/10 p-4 rounded mb-6 flex items-center gap-4">
        <span className="text-white/60">IP Imprimante:</span>
        <input
          type="text"
          value={printerIP}
          onChange={(e) => setPrinterIP(e.target.value)}
          className="bg-white/10 text-white px-3 py-1 rounded border border-white/20 w-40"
        />
        <button
          onClick={clearPrintHistory}
          className="text-sm text-white/40 hover:text-white"
        >
          Effacer historique
        </button>
      </div>

      {/* Liste des commandes */}
      <div className="grid gap-4">
        {orders.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-white/40 text-xl">Aucune commande en attente</p>
            <p className="text-white/20 mt-2">Les nouvelles commandes apparaîtront ici</p>
          </div>
        ) : (
          orders.map(order => (
            <div 
              key={order.id} 
              className={`p-6 rounded-lg border-4 ${
                order.status === 'paid' ? 'bg-brand-red/20 border-brand-red' :
                order.status === 'preparing' ? 'bg-brand-yellow/20 border-brand-yellow' :
                'bg-green-600/20 border-green-600'
              }`}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <span className="font-heading text-4xl text-white">#{order.order_number.slice(-4)}</span>
                  <span className={`ml-4 px-3 py-1 rounded text-sm uppercase ${
                    order.status === 'paid' ? 'bg-brand-red text-white' :
                    order.status === 'preparing' ? 'bg-brand-yellow text-brand-navy' :
                    'bg-green-600 text-white'
                  }`}>
                    {order.status === 'paid' ? 'NOUVELLE' :
                     order.status === 'preparing' ? 'EN COURS' : 'PRÊTE'}
                  </span>
                </div>
                <div className="text-right">
                  <p className="font-heading text-2xl text-brand-yellow">{order.pickup_time}</p>
                  <p className="text-white/60">{order.customer_name}</p>
                </div>
              </div>

              {/* Articles */}
              <div className="bg-black/20 p-4 rounded mb-4">
                {order.items.map((item, i) => (
                  <div key={i} className="mb-2">
                    <span className="font-bold text-white text-lg">{item.quantity}x {item.product_name}</span>
                    {item.options && item.options.length > 0 && (
                      <p className="text-brand-yellow text-sm ml-4">
                        → {item.options.map(o => o.choices?.join(', ')).join(' • ')}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => reprintOrder(order)}
                  className="px-4 py-2 bg-white/20 text-white rounded hover:bg-white/30"
                >
                  🖨️ Réimprimer
                </button>
                
                {order.status === 'paid' && (
                  <button
                    onClick={async () => {
                      const token = localStorage.getItem("admin_token");
                      await axios.patch(`${API}/admin/orders/${order.id}?status=preparing`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      fetchOrders();
                    }}
                    className="px-4 py-2 bg-brand-yellow text-brand-navy rounded font-bold"
                  >
                    → EN PRÉPARATION
                  </button>
                )}
                
                {order.status === 'preparing' && (
                  <button
                    onClick={async () => {
                      const token = localStorage.getItem("admin_token");
                      await axios.patch(`${API}/admin/orders/${order.id}?status=ready`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      fetchOrders();
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded font-bold"
                  >
                    ✓ PRÊTE
                  </button>
                )}

                {order.status === 'ready' && (
                  <button
                    onClick={async () => {
                      const token = localStorage.getItem("admin_token");
                      await axios.patch(`${API}/admin/orders/${order.id}?status=completed`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      fetchOrders();
                    }}
                    className="px-4 py-2 bg-white/10 text-white rounded"
                  >
                    Terminer
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ==================== APP ====================

function App() {
  return (
    <CartProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/menu/:slug" element={<MenuPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/order/:id" element={<OrderConfirmationPage />} />
            
            {/* Admin Routes */}
            <Route path="/admin" element={<AdminLoginPage />} />
            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
            <Route path="/admin/orders" element={<AdminOrdersPage />} />
            <Route path="/admin/menu" element={<AdminMenuPage />} />
            <Route path="/admin/reports" element={<AdminReportsPage />} />
            <Route path="/admin/kitchen" element={<KitchenPage />} />
          </Routes>
        </BrowserRouter>
        <Toaster 
          position="top-center" 
          toastOptions={{
            style: {
              background: '#171537',
              color: '#F8F4ED',
              border: '2px solid #FEA600'
            }
          }}
        />
      </div>
    </CartProvider>
  );
}

export default App;
